/*
 * @Author: yongquan.zhang |  yongquan.zhang01@msxf.com
 * @Date: 2024-04-16 14:07:07
 * @Last Modified by: Jay
 * @Last Modified time: 2024-08-Fr 11:34:45
 */
import request from '@common/axios/requestUtil';
import { SUCCESS_CODE, LOGOFF_CODE } from '../../error/constants';
import { msLogger } from '../../services/log';
import loading from './loading';
import { msToast } from 'msComponents';
import {
  errCodeMap,
  handleSystemException,
  isSystemError,
  bizErrorCodeMap,
  handleErrorDialogByErrorCode,
} from '../../error/errorHandle';
import { useBaseStore } from '@common/stores/storage';
import { getABCacheOrDefault } from '@common/utils/newABtest';
import { TIMEOUT } from '@common/error/constants';
import dsBridge from '@common/assets/js/dsbridge';
import { throttle } from 'lodash-es';
import { cookieSetUser, getCookie } from '@common/stores/cookie';

// 获得响应不同耗时阶梯提示文案
function getDurationLevel(durationTime: number): string {
  let durationLevel = '请求耗时低于1s';
  // 耗时打印阶梯阈值
  const level = [30, 20, 15, 10, 8, 6, 5, 4, 3, 2, 1].find((level) => {
    return durationTime >= level * 1000;
  });
  if (level) {
    durationLevel = `请求耗时超过${level}s`;
  }
  return durationLevel;
}

// 刷新vtoken等cookie的有效期，增加节流5分钟处理，避免频繁调用刷新
const refreshCookie = throttle(function () {
  const vtoken = getCookie('vtoken');
  if (vtoken) {
    cookieSetUser(vtoken);
  }
}, 300000);

function requestManager() {
  // 处理日志入参，剔除部分长参数，如图片base64字符串
  function ignoreFields(data = {}) {
    const logReq = {};
    if (data && Object.prototype.toString.call(data) === '[object Object]') {
      for (const key in data) {
        // 忽略打印的入参字段
        if (['cardFrontImg', 'cardBackImg', 'faceImg', 'applyZySdkFaceReq'].includes(key)) {
          logReq[key] = 'ignore';
        } else {
          logReq[key] = data[key];
        }
      }
    }
    return logReq;
  }

  function init() {
    request.encodeUrlList([
      '/withdraw/vayh/cash/V1.0/amortization/calculate',
      'vayh/apply/V1.0/shuntAuth',
      'vayh/sms/V1.0/binding',
      'vayh/sms/V1.0/smscode',
      '/pay/quickPaySms/V1/create',
      '/pay/wapOrder/V1/submit',
      'vayh/pwd/V1.0/trade/reset',
      '/vayh/my/V1.0/aes/profileforhidden',
      '/vayhapi/vayh/my/V1.0/aes/profileforhidden',
      '/vayh/bank/V1.0/aes/bankCardNumberOCR',
      '/vayhapi/vayh/bank/V1.0/aes/bankCardNumberOCR',
      '/withdraw/vayh/cash/V1.0/submitCashApply',
      '/unionloan/loanRouteSerial/V1/query',
      '/icr/cash/apply/V1/submit',
      '/bill/singleContractCalculate/V1/submit',
      '/loanreceipt/amortization/V1/submit',
      '/bill/amortization/V1/submit',
      '/cash/cashLimit/V1/query',
      '/cash/calculate/V1/query',
      '/aiServer/agent/cash/V1/loan',
    ]);
    request.init({
      onBeforeRequest: (info) => {
        if (info.options?.hasLoad) {
          loading.start(info.options.loadTitle);
        }
        // 日志入参，加密前
        if (!info.options) info.options = {};
        // 请求时间
        info.options.requestTime = Date.now();
        info.options.timeout = TIMEOUT;
      },
      onAfterRequest: (info) => {
        if (info.options?.hasLoad) {
          loading.destory();
        }
        const data = info.response?.data;
        const success = info.response?.data.code == SUCCESS_CODE;
        const { channel, appKey, cooperatorId, productCode, userId } = useBaseStore();
        getABCacheOrDefault('vayhFrontendLogResponseMaxLength', 3000).then(
          (_logResponseMaxLength) => {
            let res: any = '忽略，请查看api日志';
            if (success) {
              if (JSON.stringify(data).length > _logResponseMaxLength) {
                res = `忽略，长度超过${_logResponseMaxLength}`;
              } else {
                res = data;
              }
            } else {
              res = {
                code: data?.code,
                msg: data?.msg,
                message: data?.message,
              };
            }
            // 计算请求耗时
            const responseTime = Date.now();
            const requestTime = info.options?.requestTime ?? responseTime;
            const durationTime = responseTime - requestTime;
            msLogger('info', {
              time: requestTime,
              durationTime: `${durationTime}ms`,
              durationLevel: getDurationLevel(durationTime),
              message: `平台化前端接口调用成功${success ? '' : '[业务异常]'}`,
              userId,
              channel,
              appKey,
              cooperatorId,
              productCode,
              page: location.pathname,
              vtoken: info.headers?.['X-VToken'],
              url: info.url ?? '',
              req: ignoreFields(info.params),
              res,
            });
          },
        );
      },
      onRequestError: (info) => {
        if (info.options?.hasLoad) {
          loading.destory();
        }

        const { channel, appKey, cooperatorId, productCode, userId } = useBaseStore();
        // 计算请求耗时
        const responseTime = Date.now();
        const requestTime = info.options?.requestTime ?? responseTime;
        const durationTime = responseTime - requestTime;
        msLogger('error', {
          time: requestTime,
          durationTime: `${durationTime}ms`,
          durationLevel: getDurationLevel(durationTime),
          message: '平台化前端接口调用异常',
          userId,
          channel,
          appKey,
          cooperatorId,
          productCode,
          vtoken: info.headers?.['X-VToken'],
          page: location.pathname,
          url: info.url ?? '',
          req: ignoreFields(info.params),
          error: info.error?.response?.statusText ?? info.error?.message,
          type: info.error?.name,
          status: info.error?.status || info.error?.response?.status,
          stack: info.error?.stack,
        });
        if (info.options?.errorCodeInfo?.systemError) {
          info.options.errorCodeInfo.systemError();
          return;
        }
        // TODO: 应用级别异常，一般不会到这里
        if (info.options?.errDialog && !info.options?.ignoreError) {
          if (window.navigator?.onLine) {
            msToast({
              message: '系统开小差了~',
            });
          } else {
            msToast({
              message: '网络异常，请检查网络是否正确连接',
            });
          }
        }
        // TODO @zwj 新增：非本地开发环境，才需要跳转
        // handleCommonException();
      },
      onResponse: (info) => {
        refreshCookie();
        const data = info.response?.data;
        const options = info.options;
        if (!data) return null;
        if (data.code == SUCCESS_CODE) return data;
        if (data.code === LOGOFF_CODE) {
          // 用户已注销场景，通知app
          if (dsBridge.hasNativeMethod('UserInfo.closeAccountSuccess')) {
            dsBridge.call('UserInfo.closeAccountSuccess');
          }
        }

        // ignore all error
        if (info.options?.ignoreError) {
          return data;
        }

        // first step: check user has set code info. if have set callback and return  by allen
        if (info.options?.errorCodeInfo?.[data.code]) {
          info.options.errorCodeInfo[data.code](data);
          return data;
        }

        // sencond step: check eroor code is network error or login error, if it jump to error page
        // 先看看是否网络超时 or 未登录
        if (isSystemError(data.code)) {
          handleSystemException(data.code, options);
          // 这里return 将来可能要改，现在的交互是重定向到通用异常页面，将来也可能是错误状态包裹页面
          return data;
        }

        // third step: check error map has error code, if have run error map setting
        const exceptionHandle = errCodeMap?.[data.code];
        // // 如果需要重定向
        // if (exceptionHandle?.href) {
        //   location.href = exceptionHandle?.href;
        // } else if (exceptionHandle?.fn) {
        //   // 有自定义方法，则执行方法
        //   exceptionHandle.fn(data);
        // } else if (exceptionHandle?.msg) {
        //   msToast({
        //     message: exceptionHandle?.msg,
        //   });
        // }
        if (exceptionHandle && options?.errDialog) {
          handleErrorDialogByErrorCode(data, exceptionHandle);
          return data;
        }

        // fouth step: controllre error message by request options
        if (options?.ignoreErrorCode) {
          // 如果配置no code
          return data;
        }
        // 如果某个业务有单独定制白名单code，则放行操作，直接向上抛出
        if (options?.ignoreErrorCodeList && options?.ignoreErrorCodeList.includes(data.code)) {
          return data;
        }
        // 如果code不在无需处理的异常code当中
        if (!bizErrorCodeMap.get(data.code) && options?.errDialog) {
          msToast.error({
            message: data.message,
            duration: 2000,
          });
          return data;
        }
        if (info.options?.errorCodeInfo?.defalutError) {
          info.options.errorCodeInfo.defalutError(data);
          return data;
        }
        if (options?.ignoreBizErrorCode || !bizErrorCodeMap.get(data.code)) {
          return data;
        }
        // 如果是业务异常
        const customResponse = {
          code: data.code || -1,
          parsedMsg: { message: '', code: '' },
        };
        // 这里也可以用前端的异常message
        customResponse.parsedMsg.message = data.message;
        customResponse.parsedMsg.code = data.code;
        // 后端可能会包裹一层msg：例如： "message": "{\"msg\":\"尊敬的巴拉巴拉一大堆\",\"title\":\"失败\"}",
        // 也可能例如： "{"msg":"尊敬的巴拉巴拉一大堆","title":"失败"}"
        const msgStrReg = /\\"msg\\"/;
        const msgStrEscapeReg = /"msg"/;
        if (msgStrReg.test(data.message) || msgStrEscapeReg.test(data.message)) {
          customResponse.parsedMsg.message = JSON.parse(data.message)?.msg || '';
        }
        return {
          ...data,
          ...customResponse,
        };
      },
    });
  }

  return {
    init,
  };
}

export default requestManager();
